import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';


const Submit = ({
  formData,
  onFormDataChange,
  onSubmit,
  onBack
}) => {
  const [contactInfo, setContactInfo] = useState({
    name: formData.contactInfo?.name || '',
    phone: formData.contactInfo?.phone || '',
    email: formData.contactInfo?.email || '',
    preferredContactMethod: formData.contactInfo?.preferredContactMethod || 'phone'
  });

  const [paymentMethod, setPaymentMethod] = useState(formData.paymentMethod || '');

  const [gratuity, setGratuity] = useState({
    type: formData.gratuity?.type || 'percentage',
    amount: formData.gratuity?.amount || '25%'
  });

  const [reasonForShopping, setReasonForShopping] = useState(formData.reasonForShopping || '');

  // Initialize form data with default values if not already set
  useEffect(() => {
    const updates = {};
    if (!formData.contactInfo) {
      updates.contactInfo = contactInfo;
    }
    if (!formData.gratuity) {
      updates.gratuity = gratuity;
    }

    if (Object.keys(updates).length > 0) {
      onFormDataChange({ ...formData, ...updates });
    }
  }, []);

  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [selectedPaymentIndex, setSelectedPaymentIndex] = useState(-1);
  const [errorMessage, setErrorMessage] = useState('');

  const paymentDropdownRef = useRef(null);

  const handleContactChange = (field, value) => {
    const newContactInfo = { ...contactInfo, [field]: value };
    setContactInfo(newContactInfo);
  };

  const handleGratuityChange = (type, amount) => {
    const newGratuity = { type, amount };
    setGratuity(newGratuity);
  };

  const handleReasonForShoppingChange = (value) => {
    setReasonForShopping(value);
  };

  const paymentOptions = [
    { label: 'Credit/Debit Card', value: 'credit_card' },
    { label: 'Direct PO Payment', value: 'direct-po-payment' },
    { label: 'Other', value: 'other' }
  ];

  const handlePaymentSelect = (payment) => {
    setPaymentMethod(payment.value);
    setShowPaymentDropdown(false);
    setSelectedPaymentIndex(-1);
  };

  const handlePaymentKeyDown = (e) => {
    if (!showPaymentDropdown || paymentOptions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedPaymentIndex(prev => prev < paymentOptions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedPaymentIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedPaymentIndex >= 0 && selectedPaymentIndex < paymentOptions.length) {
          handlePaymentSelect(paymentOptions[selectedPaymentIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowPaymentDropdown(false);
        break;
      default:
        break;
    }
  };

  const handleClickOutside = (event) => {
    if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(event.target)) {
      setShowPaymentDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = () => {
    setErrorMessage('');
    if (!contactInfo.name || !contactInfo.phone || !paymentMethod) {
      setErrorMessage('Please fill in all required contact information');
      return;
    }

    // Update form data with all current step data before submitting
    onFormDataChange({
      contactInfo,
      paymentMethod,
      gratuity,
      reasonForShopping
    });

    // Pass the current step data directly to onSubmit
    onSubmit({
      contactInfo,
      paymentMethod,
      gratuity,
      reasonForShopping
    });
  };

  const isFormValid = () => {
    return contactInfo.name && contactInfo.phone && contactInfo.phone && paymentMethod;
  };


  return (
    <div className="pt-3">

      <h2 className="text-lg font-semibold text-white mb-3 font-montserratBold">
        Enter your contact information
      </h2>

      <div className="space-y-8 mb-8">
        {/* Contact Information */}
        <div className="space-y-3">
          <div className='border-[1px] border-[#5A3770] bg-[#2C0C37] rounded-[40px] flex gap-2 items-center justify-center px-2 min-h-[52px] pl-4'>
            <input
              id="name"
              type="text"
              className="text-[#B4A5BB] w-full bg-transparent outline-[0] h-11 text-base font-medium font-montserratMedium selection:text-white selection:bg-[#5A3770] autofill:!text-[#B4A5BB] autofill:!bg-transparent"
              style={{
                WebkitTextFillColor: '#B4A5BB',
                WebkitBoxShadow: '0 0 0 1000px transparent inset',
                transition: 'none',
                WebkitTransition: 'none'
              }}
              value={contactInfo.name}
              onChange={(e) => handleContactChange('name', e.target.value)}
              placeholder="First & Last Name *"
            />
          </div>

          <div className='border-[1px] border-[#5A3770] bg-[#2C0C37] rounded-[40px] flex gap-2 items-center justify-center px-2 min-h-[52px] pl-4 relative'>
            <input
              id="phone"
              type="tel"
              className="text-[#B4A5BB] w-full bg-transparent outline-[0] h-11 text-base font-medium font-montserratMedium selection:text-white selection:bg-[#5A3770] autofill:!text-[#B4A5BB] autofill:!bg-transparent"
              style={{
                WebkitTextFillColor: '#B4A5BB',
                WebkitBoxShadow: '0 0 0 1000px transparent inset',
                transition: 'none',
                WebkitTransition: 'none'
              }}
              value={contactInfo.phone}
              onChange={(e) => handleContactChange('phone', e.target.value)}
              placeholder="Phone Number *"
            />
          </div>

          <div className='border-[1px] border-[#5A3770] bg-[#2C0C37] rounded-[40px] flex gap-2 items-center justify-center px-2 min-h-[52px] pl-4'>
            <input
              id="email"
              type="email"
              className="text-[#B4A5BB] w-full bg-transparent outline-[0] h-11 text-base font-medium font-montserratMedium selection:text-white selection:bg-[#5A3770] autofill:!text-[#B4A5BB] autofill:!bg-transparent"
              style={{
                WebkitTextFillColor: '#B4A5BB',
                WebkitBoxShadow: '0 0 0 1000px transparent inset',
                transition: 'none',
                WebkitTransition: 'none'
              }}
              value={contactInfo.email}
              onChange={(e) => handleContactChange('email', e.target.value)}
              placeholder="Email Address *"
            />
          </div>

          <div className="relative" ref={paymentDropdownRef}>
            <div
              className="border-[1px] border-[#5A3770] bg-[#2C0C37] rounded-[40px] flex gap-2 items-center justify-center px-2 min-h-[52px] pl-4 cursor-pointer"
              onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
              onKeyDown={handlePaymentKeyDown}
              tabIndex={0}
              role="button"
              aria-haspopup="listbox"
              aria-expanded={showPaymentDropdown}
            >
              <input
                type="text"
                className="text-[#B4A5BB] w-full bg-transparent outline-[0] h-11 text-base font-medium font-montserratMedium cursor-pointer"
                value={paymentOptions.find(option => option.value === paymentMethod)?.label || ''}
                placeholder="Payment Method *"
                readOnly
              />
              <svg className="w-4 h-4 text-[#B4A5BB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {showPaymentDropdown && paymentOptions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-[#2C0C37] border border-[#5A3770] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {paymentOptions.map((option, index) => (
                  <div
                    key={option.value}
                    className={`px-4 py-3 cursor-pointer text-[#B4A5BB] hover:bg-[#5A3770] hover:text-white transition-colors ${
                      index === selectedPaymentIndex ? 'bg-[#5A3770] text-white' : ''
                    }`}
                    onClick={() => {
                      handlePaymentSelect(option);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handlePaymentSelect(option);
                      }
                    }}
                    onMouseEnter={() => setSelectedPaymentIndex(index)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Preferred Contact Method */}
        <div className="space-y-3">
          <h3 className="text-base text-white mb-4 font-bold font-montserratBold">
            Preferred Contact Method *
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Phone', value: 'phone' },
              { label: 'Email', value: 'email' }
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                className={`
                  px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 w-auto min-h-[60px] border-[1px] border-solid text-white
                  ${contactInfo.preferredContactMethod === option.value
                    ? 'border-[#E01766] bg-[#E01766]'
                    : 'border-[#5A3770] bg-[#461D58]'
                  }
                `}
                onClick={() => handleContactChange('preferredContactMethod', option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Reason for Shopping */}
        <div className="space-y-3">
          <h3 className="text-base text-white mb-4 font-bold font-montserratBold">
            Reason for Shopping
          </h3>
          <textarea
            id="reasonForShopping"
            name="reasonForShopping"
            className="w-full h-24 px-4 py-3 border border-[#5A3770] rounded-lg bg-[#2C0C37] text-white placeholder-[#B4A5BB] focus:outline-none focus:ring-2 focus:ring-[#E01766] focus:border-transparent resize-none"
            placeholder="Tell us about your occasion or reason for ordering..."
            value={reasonForShopping}
            onChange={(e) => handleReasonForShoppingChange(e.target.value)}
          />
        </div>

        {/* Gratuity Section */}
        <div className="">
          <h3 className="text-base text-white mb-4 font-bold font-montserratBold">
            Gratuity for the delivery driver (Recommended)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {['18%', '20%', '25%'].map((percentage) => (
              <button
                key={percentage}
                type="button"
                className={`
                  px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 w-auto min-h-[60px] border-[1px] border-solid text-white
                  ${gratuity.amount === percentage && gratuity.type === 'percentage'
                    ? 'border-[#E01766] bg-[#E01766]'
                    : 'border-[#5A3770] bg-[#461D58]'
                  }
                `}
                onClick={() => handleGratuityChange('percentage', percentage)}
              >
                {percentage}
              </button>
            ))}
            <button
              type="button"
              className={`
                px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 w-auto min-h-[60px] border-[1px] border-solid text-white
                ${gratuity.type === 'custom'
                  ? 'border-[#E01766] bg-[#E01766]'
                  : 'border-[#5A3770] bg-[#461D58]'
                }
              `}
              onClick={() => handleGratuityChange('custom', gratuity.amount)}
            >
              Custom
            </button>
          </div>
          {gratuity.type === 'custom' && (
            <div className='border-[1px] border-[#5A3770] bg-[#2C0C37] rounded-[40px] flex gap-2 items-center justify-center px-2 min-h-[52px] pl-4 relative'>
              <input
                id="gratuityAmount"
                type="text"
                className="text-[#B4A5BB] w-full bg-transparent outline-[0] h-11 text-base font-medium font-montserratMedium selection:text-white selection:bg-[#5A3770] autofill:!text-[#B4A5BB] autofill:!bg-transparent"
                style={{
                  WebkitTextFillColor: '#B4A5BB',
                  WebkitBoxShadow: '0 0 0 1000px transparent inset',
                  transition: 'none',
                  WebkitTransition: 'none'
                }}
                value={gratuity.amount}
                onChange={(e) => handleGratuityChange('custom', e.target.value)}
                placeholder="Gratuity Amount"
              />
            </div>
          )}
        </div>

      </div>

      {errorMessage && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {errorMessage}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          className="px-4 items-center justify-center  flex w-auto bg-transparent text-white text-center py-2 h-11 font-bold font-montserratBold border-[1px] border-solid border-[#F6F3F7]"
          onClick={() => {
            // Save current state before going back
            onFormDataChange({
              contactInfo,
              paymentMethod,
              gratuity,
              reasonForShopping
            });
            onBack();
          }}
        >
          Back
        </button>
        <button
          type="button"
          className={`
            dark:bg-button px-4 items-center justify-center flex gap-2 w-auto bg-button text-white text-center py-2 h-11 font-bold font-montserratBold
            ${isFormValid()
              ? 'bg-button text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }
          `}
          onClick={handleSubmit}
          disabled={!isFormValid()}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Submit
        </button>
      </div>

    </div>
  );
};

Submit.propTypes = {
  formData: PropTypes.shape({
  contactInfo: PropTypes.shape({
    name: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    preferredContactMethod: PropTypes.string
  }),
  paymentMethod: PropTypes.string,
    gratuity: PropTypes.shape({
      type: PropTypes.string,
      amount: PropTypes.string
    }),
    reasonForShopping: PropTypes.string
  }).isRequired,
  onFormDataChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default Submit;
